<script lang="ts" setup>
import { createReusableTemplate, useMediaQuery } from '@vueuse/core'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type {CityType} from "~/types/Geo";

const userStore = useUserStore();
const globalStore = useGlobalStore()
const route = useRoute()

const props = defineProps({
  btn_class: {
    type: String,
    default: null
  },
})

const { locale } = useI18n()

const [UseTemplate, LocationsList] = createReusableTemplate()
const isDesktop = ref(true) // По умолчанию true для SSR
onMounted(() => {
  const mediaQuery = useMediaQuery('(min-width: 768px)')
  watchEffect(() => {
    isDesktop.value = mediaQuery.value
  })
})
const isOpen = ref(false)

function onLocationSelect(city: CityType) {
    userStore.setCity(city)
    isOpen.value = false
    if(route.params.city) {
      navigateTo({name: route.params.name as string, params: {slug: route.params.slug, city: userStore.currentCity?.slug}})
    }
}

</script>

<template>
  <div>
    <UseTemplate>
      <Command :dir="locale === 'ar' ? 'rtl' : 'ltr'">
        <CommandInput ::placeholder="$t('Filter locations...')"  />
        <CommandList>
          <CommandEmpty>{{$t('No results found')}}</CommandEmpty>
          <CommandGroup v-for="country in globalStore.locationsList" :heading="country.name">
            <template v-for="region in country.regions">
              <CommandItem
                v-for="city in region.cities"
                :value="city.name"
                @select="onLocationSelect(city)"
                :disabled="city.documentId === userStore.currentCity?.documentId"
            >
              {{ city.name }}
            </CommandItem>
            </template>
          </CommandGroup>
        </CommandList>
      </Command>
    </UseTemplate>

    <Popover v-if="isDesktop" v-model:open="isOpen">
      <PopoverTrigger as-child>
        <Button variant="ghost" class="w-fit justify-start border-0 px-2" :class="props.btn_class || 'max-h-[36px] px-2'">
            <Icon size="20" name="ri:map-pin-2-line" class="mr-2 rtl:ml-2" :class="{'text-primary-500':isOpen}" />
            {{ userStore.currentCity ? userStore.currentCity.name : $t('Select location') }}
            <Icon size="18" name="ri:arrow-down-s-line" class="ml-2 rtl:mr-2 shrink-0 opacity-50"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-[200px] p-0" align="start">
        <LocationsList />
      </PopoverContent>
    </Popover>

    <Drawer v-else :open="isOpen" @update:open="(newOpenValue) => isOpen = newOpenValue">
      <DrawerTrigger as-child>
        <Button variant="ghost" class="w-fit justify-start border-0 px-2" :class="props.btn_class || 'max-h-[36px] px-2'">
            <Icon size="20" name="ri:map-pin-2-line" class="sm:mr-2 rtl:ml-2" :class="{'text-primary-500':isOpen}" />
            <span class="hidden sm:inline">{{ userStore.currentCity ? userStore.currentCity.name : $t('Select location') }}</span>
            <Icon size="18" name="ri:arrow-down-s-line" class="sm:ml-2 rtl:mr-2 shrink-0 opacity-50"/>
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <div class="mt-4 border-t">
          <LocationsList />
        </div>
      </DrawerContent>
    </Drawer>
  </div>
</template>